import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Intro.css';

/* ── 카카오 채널 URL (추후 교체) ─────────── */
const KAKAO_CHANNEL_URL = 'https://pf.kakao.com/_xbGxmxdG/chat';

/* ── 카운트업 훅 ─────────────────────────── */
const useCountUp = (target, duration = 1400) => {
    const [count, setCount] = useState(0);
    const [started, setStarted] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) setStarted(true);
            },
            { threshold: 0.5 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!started) return;
        let startTime = null;
        const step = ts => {
            if (!startTime) startTime = ts;
            const progress = Math.min((ts - startTime) / duration, 1);
            const ease = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(ease * target));
            if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [started, target, duration]);

    return { count, ref };
};

/* ── 스크롤 reveal 훅 ───────────────────── */
const useReveal = (threshold = 0.12) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [threshold]);
    return { ref, visible };
};

/* ── 미니 프리뷰 컴포넌트들 ─────────────── */
const PreviewShell = ({ title, children }) => (
    <div className="preview-shell">
        <div className="preview-topbar">
            <span className="preview-dot" style={{ background: '#ff5f57' }} />
            <span className="preview-dot" style={{ background: '#febc2e' }} />
            <span className="preview-dot" style={{ background: '#28c840' }} />
            <span className="preview-shell-title">{title}</span>
        </div>
        <div className="preview-body">{children}</div>
    </div>
);

const Preview_KeywordRank = () => (
    <PreviewShell title="키워드 추출순위">
        <table className="preview-table">
            <thead>
                <tr>
                    <th>키워드</th>
                    <th>순위</th>
                    <th>PC 검색량</th>
                    <th>MO 검색량</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td className="preview-kw">무선청소기</td>
                    <td>
                        <span className="preview-rank-badge r-top">1위</span>
                    </td>
                    <td>45,200</td>
                    <td>98,400</td>
                </tr>
                <tr>
                    <td className="preview-kw">로봇청소기</td>
                    <td>
                        <span className="preview-rank-badge r-good">3위</span>
                    </td>
                    <td>32,100</td>
                    <td>71,000</td>
                </tr>
                <tr>
                    <td className="preview-kw">핸디청소기</td>
                    <td>
                        <span className="preview-rank-badge r-mid">7위</span>
                    </td>
                    <td>18,400</td>
                    <td>41,200</td>
                </tr>
                <tr>
                    <td className="preview-kw">청소기 추천</td>
                    <td>
                        <span className="preview-rank-badge r-out">순위권 밖</span>
                    </td>
                    <td>67,800</td>
                    <td>143,600</td>
                </tr>
            </tbody>
        </table>
    </PreviewShell>
);

const HISTORY_DATA = [
    { date: '5/12', rank: 12 },
    { date: '5/13', rank: 9 },
    { date: '5/14', rank: 7 },
    { date: '5/15', rank: 4 },
    { date: '5/16', rank: 5 },
    { date: '5/17', rank: 3 },
    { date: '5/18', rank: 2 },
];

const Preview_History = () => {
    const maxRank = 15;
    const W = 240,
        H = 60,
        pad = 10;
    const xs = HISTORY_DATA.map((_, i) => pad + (i / (HISTORY_DATA.length - 1)) * (W - pad * 2));
    const ys = HISTORY_DATA.map(d => pad + ((d.rank - 1) / (maxRank - 1)) * (H - pad * 2));
    const linePath = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x},${ys[i]}`).join(' ');
    const areaPath = `${linePath} L${xs[xs.length - 1]},${H} L${xs[0]},${H} Z`;

    return (
        <PreviewShell title="과거 순위 조회">
            <div className="preview-history-keyword">
                <span className="preview-history-kw-chip">무선청소기</span>
                <span className="preview-history-kw-chip">스마트스토어A</span>
            </div>
            <div className="preview-stat-row">
                <div
                    className="preview-mini-stat"
                    style={{ background: '#f0fdf4', color: '#16a34a' }}
                >
                    <span className="preview-mini-stat-val">2위</span>
                    <span className="preview-mini-stat-label">최고</span>
                </div>
                <div
                    className="preview-mini-stat"
                    style={{ background: '#fef2f2', color: '#dc2626' }}
                >
                    <span className="preview-mini-stat-val">12위</span>
                    <span className="preview-mini-stat-label">최저</span>
                </div>
                <div
                    className="preview-mini-stat"
                    style={{ background: '#fffbeb', color: '#d97706' }}
                >
                    <span className="preview-mini-stat-val">6위</span>
                    <span className="preview-mini-stat-label">평균</span>
                </div>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="preview-sparkline">
                <defs>
                    <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#4f46e5" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <path d={areaPath} fill="url(#sparkGrad)" />
                <path
                    d={linePath}
                    fill="none"
                    stroke="#4f46e5"
                    strokeWidth="2"
                    strokeLinejoin="round"
                />
                {xs.map((x, i) => (
                    <circle key={i} cx={x} cy={ys[i]} r="3" fill="#4f46e5" />
                ))}
            </svg>
            <div className="preview-sparkline-dates">
                {HISTORY_DATA.map(d => (
                    <span key={d.date}>{d.date}</span>
                ))}
            </div>
        </PreviewShell>
    );
};

const Preview_IntegratedSearch = () => (
    <PreviewShell title="통합 키워드 검색">
        <div className="preview-integrated-head">
            <div className="preview-input-mock">
                <i className="fas fa-search" style={{ color: '#aaa', marginRight: 6 }} />
                <span>smartstore.naver.com/brandA</span>
            </div>
            <button className="preview-btn-mock">조회</button>
        </div>
        <table className="preview-table preview-table--integrated">
            <thead>
                <tr>
                    <th>순위키워드</th>
                    <th>스토어명</th>
                    <th>생성 키워드</th>
                    <th>상태</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td className="preview-kw">에어프라이어</td>
                    <td>스토어A</td>
                    <td>124개</td>
                    <td>
                        <span className="preview-status-badge done">완료</span>
                    </td>
                </tr>
                <tr>
                    <td className="preview-kw">무선청소기</td>
                    <td>스토어A</td>
                    <td>89개</td>
                    <td>
                        <span className="preview-status-badge done">완료</span>
                    </td>
                </tr>
                <tr>
                    <td className="preview-kw">핸드폰케이스</td>
                    <td>스토어A</td>
                    <td className="preview-loading-cell">
                        <span className="preview-spinner" />
                        조회중
                    </td>
                    <td>
                        <span className="preview-status-badge loading">진행중</span>
                    </td>
                </tr>
            </tbody>
        </table>
    </PreviewShell>
);

const Preview_ProductAnalysis = () => (
    <PreviewShell title="상품 분석">
        <div className="preview-analysis-layout">
            <div className="preview-score-ring">
                <svg viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="32" fill="none" stroke="#e8ecf4" strokeWidth="7" />
                    <circle
                        cx="40"
                        cy="40"
                        r="32"
                        fill="none"
                        stroke="#4f46e5"
                        strokeWidth="7"
                        strokeDasharray="201"
                        strokeDashoffset="35"
                        strokeLinecap="round"
                        transform="rotate(-90 40 40)"
                    />
                </svg>
                <div className="preview-score-text">
                    <span className="preview-score-num">83</span>
                    <span className="preview-score-sub">점</span>
                </div>
            </div>
            <div className="preview-analysis-items">
                {[
                    { label: '현재 순위', val: '3위', color: '#4f46e5' },
                    { label: '리뷰 점수', val: '4.8점', color: '#16a34a' },
                    { label: '가격 경쟁력', val: '상위 12%', color: '#d97706' },
                    { label: '키워드 매칭', val: '87점', color: '#2e5bff' },
                    {
                        label: 'AI 진단',
                        val: '상위 노출 적합',
                        color: '#722ed1',
                        icon: 'fas fa-robot',
                    },
                ].map(row => (
                    <div key={row.label} className="preview-analysis-row">
                        <span className="preview-analysis-label">{row.label}</span>
                        <span className="preview-analysis-val" style={{ color: row.color }}>
                            {row.icon && <i className={row.icon} style={{ marginRight: 3 }} />}
                            {row.val}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    </PreviewShell>
);

/* ── 데이터 ─────────────────────────────── */
// eslint-disable-next-line no-unused-vars, unused-imports/no-unused-vars
const ORBS = [
    { size: 90, top: '15%', left: '8%', delay: '0s', dur: '9s', color: 'rgba(46,91,255,0.18)' },
    { size: 55, top: '65%', left: '75%', delay: '-3s', dur: '11s', color: 'rgba(19,194,194,0.15)' },
    { size: 70, top: '40%', left: '88%', delay: '-5s', dur: '8s', color: 'rgba(114,46,209,0.13)' },
    { size: 40, top: '80%', left: '20%', delay: '-2s', dur: '12s', color: 'rgba(46,91,255,0.12)' },
    { size: 110, top: '5%', left: '55%', delay: '-6s', dur: '14s', color: 'rgba(79,70,229,0.1)' },
    { size: 35, top: '55%', left: '40%', delay: '-1s', dur: '7s', color: 'rgba(19,194,194,0.12)' },
];

const SHOWCASES = [
    {
        tag: '순위 조회',
        tagColor: '#2e5bff',
        title: '키워드별 순위를\n한 번에 확인',
        desc: '네이버 쇼핑에서 내 상품이 몇 위에 있는지 키워드별로 한번에 조회합니다. PC·모바일 검색량까지 함께 확인하여 키워드 전략을 세울 수 있습니다.',
        preview: <Preview_KeywordRank />,
        reverse: false,
    },
    {
        tag: '순위 이력',
        tagColor: '#4f46e5',
        title: '30일 순위 변동을\n차트로 분석',
        desc: '날짜별 순위 이력을 꺾은선 차트로 시각화합니다. 최고·최저·평균 순위를 한눈에 파악하고 트렌드 변화를 추적할 수 있습니다.',
        preview: <Preview_History />,
        reverse: true,
    },
    {
        tag: '통합 검색',
        tagColor: '#13c2c2',
        title: '스토어 전체 키워드를\n통합 추출',
        desc: '스마트스토어 URL만 입력하면 순위 키워드부터 연관 키워드까지 자동으로 수집합니다. 수십 개 상품을 동시에 처리하는 배치 조회를 지원합니다.',
        preview: <Preview_IntegratedSearch />,
        reverse: false,
    },
    {
        tag: '상품 분석',
        tagColor: '#722ed1',
        title: 'AI 기반 상품\n종합 분석 리포트',
        desc: '순위·리뷰·가격·키워드 매칭을 종합한 분석 리포트를 제공합니다. AI 진단으로 상위 노출을 위한 개선 포인트를 즉시 파악할 수 있습니다.',
        preview: <Preview_ProductAnalysis />,
        reverse: true,
    },
];

const FEATURES = [
    {
        icon: 'fas fa-search',
        title: '키워드 추출순위 Ver1',
        desc: '네이버 쇼핑 키워드별 상품 순위 빠른 추출',
        color: '#2e5bff',
    },
    {
        icon: 'fas fa-search-plus',
        title: '키워드 추출순위 Ver2',
        desc: 'PC·모바일 검색량 포함 정밀 순위 분석',
        color: '#2e5bff',
    },
    {
        icon: 'fas fa-globe',
        title: '키워드 통검 순위',
        desc: '네이버 통합검색 내 키워드 순위 확인',
        color: '#2e5bff',
    },
    {
        icon: 'fas fa-trophy',
        title: '네이버 순위 조회',
        desc: '특정 상품의 키워드별 현재 순위 즉시 조회',
        color: '#4f46e5',
    },
    {
        icon: 'fas fa-eye',
        title: '네이버 순위 추적',
        desc: '여러 상품 순위 변동 동시 실시간 모니터링',
        color: '#4f46e5',
    },
    {
        icon: 'fas fa-history',
        title: '과거 순위 조회',
        desc: '30일 순위 이력·트렌드 차트 확인',
        color: '#4f46e5',
    },
    {
        icon: 'fas fa-layer-group',
        title: '통합 키워드 검색',
        desc: '순위·상품·트렌드 데이터를 통합 분석',
        color: '#13c2c2',
    },
    {
        icon: 'fas fa-box-open',
        title: '상품 분석',
        desc: 'MID 기반 네이버 상품 종합 분석 리포트',
        color: '#13c2c2',
    },
    {
        icon: 'fas fa-tasks',
        title: '통합작업 키워드추출',
        desc: '다수 키워드 순위 일괄 추출',
        color: '#13c2c2',
    },
    {
        icon: 'fas fa-fingerprint',
        title: 'MID 조회',
        desc: '네이버 상품 URL에서 MID 빠른 추출',
        color: '#722ed1',
    },
    {
        icon: 'fas fa-info-circle',
        title: '상품정보 조회',
        desc: 'MID로 상품 상세 정보 즉시 확인',
        color: '#722ed1',
    },
    {
        icon: 'fas fa-link',
        title: '상품 주소 변환',
        desc: '다양한 네이버 상품 URL 표준 형식 변환',
        color: '#722ed1',
    },
    {
        icon: 'fas fa-font',
        title: '글자수 세기',
        desc: '상품명·설명 텍스트 길이 측정 및 분석',
        color: '#722ed1',
    },
];

/* ── reveal 래퍼 컴포넌트들 ────────────── */
const ShowcaseCard = ({ sc }) => {
    const { ref, visible } = useReveal(0.1);
    return (
        <div
            ref={ref}
            className={`intro-showcase-card ${sc.reverse ? 'intro-showcase-card--reverse' : ''} ${visible ? 'is-visible' : 'is-hidden'}`}
        >
            <div className="intro-showcase-info">
                <span
                    className="intro-showcase-tag"
                    style={{ background: sc.tagColor + '18', color: sc.tagColor }}
                >
                    {sc.tag}
                </span>
                <h3 className="intro-showcase-title">
                    {sc.title.split('\n').map((line, li, arr) => (
                        <React.Fragment key={li}>
                            {line}
                            {li < arr.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                </h3>
                <p className="intro-showcase-desc">{sc.desc}</p>
            </div>
            <div className="intro-showcase-preview">{sc.preview}</div>
        </div>
    );
};

const FeatureItemClean = ({ f, delay }) => {
    const { ref, visible } = useReveal(0.05);
    return (
        <div
            ref={ref}
            className={`feature-clean-item ${visible ? 'is-visible' : 'is-hidden'}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            <div
                className="feature-clean-icon"
                style={{ color: f.color, background: `${f.color}15` }}
            >
                <i className={f.icon} />
            </div>
            <div className="feature-clean-text">
                <h4 className="feature-clean-title">{f.title}</h4>
                <p className="feature-clean-desc">{f.desc}</p>
            </div>
        </div>
    );
};

/* ── 카카오 플로팅 컴포넌트 ─────────────── */
const KakaoFloat = () => {
    const [open, setOpen] = useState(true);
    return (
        <div className="kakao-wrap">
            {open && (
                <div className="kakao-callout">
                    <button className="kakao-callout-close" onClick={() => setOpen(false)}>
                        ×
                    </button>
                    <p className="kakao-callout-title">무엇이든 물어보세요 😊</p>
                    <ul className="kakao-callout-list">
                        <li>
                            <i className="fas fa-check-circle" /> 서비스 이용 문의
                        </li>
                        <li>
                            <i className="fas fa-check-circle" /> 기능 사용법 안내
                        </li>
                        <li>
                            <i className="fas fa-check-circle" /> 계정·결제 관련
                        </li>
                    </ul>
                    <span className="kakao-callout-arrow" />
                </div>
            )}
            <a
                href={KAKAO_CHANNEL_URL}
                target="_blank"
                rel="noreferrer"
                className="kakao-float"
                aria-label="카카오 채널 상담"
            >
                <svg className="kakao-float-icon" viewBox="0 0 512 512" fill="#3A1D1D">
                    <path d="M255.5 48C132.6 48 32 125.4 32 221.5c0 61.4 39.4 115.5 98.9 148.1l-24.3 89.9c-2.1 7.9 6.7 14.1 13.5 9.2l110.3-72.5c8.3.9 16.8 1.3 25.2 1.3 122.9 0 223.5-77.4 223.5-173.5S378.4 48 255.5 48z" />
                </svg>
            </a>
        </div>
    );
};

/* ── 히어로 맛보기 위젯 ─────────────────── */
// eslint-disable-next-line no-unused-vars, unused-imports/no-unused-vars
const HeroTeaser = ({ onLogin }) => {
    const [step, setStep] = useState(0);
    const [keyword, setKeyword] = useState('');
    const [url, setUrl] = useState('');
    const [submittedKw, setSubmittedKw] = useState('');

    const handleCheck = () => {
        if (!keyword.trim()) return;
        setSubmittedKw(keyword.trim());
        setStep(2);
    };

    if (step === 0)
        return (
            <div className="hero-teaser anim-btn">
                <span className="hero-teaser-q">
                    <i className="fas fa-store" /> 판매중인 상품이 있으신가요?
                </span>
                <button className="hero-teaser-yes" onClick={() => setStep(1)}>
                    있어요, 순위 확인해볼게요 <i className="fas fa-arrow-right" />
                </button>
            </div>
        );

    if (step === 1)
        return (
            <div className="hero-teaser hero-teaser--form anim-btn">
                <div className="hero-teaser-field">
                    <label htmlFor="teaser-keyword" className="hero-teaser-label">
                        대표 키워드를 입력해주세요
                    </label>
                    <input
                        id="teaser-keyword"
                        className="hero-teaser-input"
                        placeholder="예: 무선청소기"
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCheck()}
                    />
                </div>
                <div className="hero-teaser-field">
                    <label htmlFor="teaser-url" className="hero-teaser-label">
                        상품 주소 <span className="hero-teaser-optional">(선택)</span>
                    </label>
                    <input
                        id="teaser-url"
                        className="hero-teaser-input"
                        placeholder="smartstore.naver.com/..."
                        value={url}
                        onChange={e => setUrl(e.target.value)}
                    />
                </div>
                <button
                    className="hero-teaser-submit"
                    onClick={handleCheck}
                    disabled={!keyword.trim()}
                >
                    <i className="fas fa-search" /> 순위 미리보기
                </button>
            </div>
        );

    return (
        <div className="hero-teaser hero-teaser--result anim-btn">
            <div className="hero-teaser-result-head">
                <i className="fas fa-chart-line" />
                <span>&apos;{submittedKw}&apos; 순위 이력 미리보기</span>
            </div>
            <div className="hero-teaser-blur-wrap">
                <div className="hero-teaser-preview-inner">
                    <Preview_History />
                </div>
                <div className="hero-teaser-lock">
                    <i className="fas fa-lock hero-teaser-lock-icon" />
                    <p className="hero-teaser-lock-text">
                        로그인하면 30일 전체 이력을 확인할 수 있습니다
                    </p>
                    <button className="hero-teaser-login-btn" onClick={onLogin}>
                        지금 로그인하기 <i className="fas fa-arrow-right" />
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ── 메인 컴포넌트 ──────────────────────── */

/* ── 배경용 히어로 차트 ──────────────── */
const HeroBackgroundChart = () => (
    <div className="hero-bg-chart-wrapper">
        <div className="hero-grid-overlay"></div>
        <div className="aurora-blob aurora-1"></div>
        <div className="aurora-blob aurora-2"></div>
        <div className="aurora-blob aurora-3"></div>
        <svg
            viewBox="0 0 1440 600"
            preserveAspectRatio="none"
            className="hero-bg-svg"
            style={{ position: 'relative', zIndex: 1 }}
        >
            <defs>
                <linearGradient id="bgChartGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563eb" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="textShine" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#2e5bff" />
                    <stop offset="50%" stopColor="#ec4899" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                </linearGradient>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="6" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
            </defs>

            {/* Guide Lines */}
            <g className="bg-chart-guides">
                <line
                    x1="400"
                    y1="300"
                    x2="400"
                    y2="600"
                    stroke="#9ca3af"
                    strokeDasharray="4 4"
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                />
                <line
                    x1="800"
                    y1="180"
                    x2="800"
                    y2="600"
                    stroke="#9ca3af"
                    strokeDasharray="4 4"
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                />
                <line
                    x1="1200"
                    y1="80"
                    x2="1200"
                    y2="600"
                    stroke="#9ca3af"
                    strokeDasharray="4 4"
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                />
            </g>

            {/* Competitor Lines */}
            <path
                className="bg-chart-comp comp-1"
                d="M0,200 L200,250 L400,200 L600,300 L800,280 L1000,400 L1200,380 L1440,450"
                fill="none"
                stroke="#9ca3af"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
            />
            <path
                className="bg-chart-comp comp-2"
                d="M0,400 L200,420 L400,410 L600,480 L800,450 L1000,520 L1200,550 L1440,580"
                fill="none"
                stroke="#d1d5db"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
            />

            {/* Main Area & Line */}
            <path
                className="bg-chart-area"
                d="M0,600 L0,500 L200,460 L400,300 L600,350 L800,180 L1000,220 L1200,80 L1440,20 L1440,600 Z"
                fill="url(#bgChartGrad)"
            />
            <path
                className="bg-chart-line"
                d="M0,500 L200,460 L400,300 L600,350 L800,180 L1000,220 L1200,80 L1440,20"
                fill="none"
                stroke="url(#textShine)"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#glow)"
                vectorEffect="non-scaling-stroke"
            />
        </svg>

        {/* HTML Nodes overlay to prevent SVG distortion on mobile */}
        <div className="bg-chart-html-nodes">
            <div
                className="html-node"
                style={{ left: '13.88%', top: '76.66%', animationDelay: '0.6s' }}
            >
                <div className="node-circle pink-node"></div>
                <div className="node-label start-label">순위권 밖</div>
            </div>
            <div
                className="html-node"
                style={{ left: '27.77%', top: '50%', animationDelay: '0.9s' }}
            >
                <div className="node-circle pink-node"></div>
            </div>
            <div
                className="html-node"
                style={{ left: '41.66%', top: '58.33%', animationDelay: '1.2s' }}
            >
                <div className="node-circle purple-node"></div>
            </div>
            <div
                className="html-node"
                style={{ left: '55.55%', top: '30%', animationDelay: '1.5s' }}
            >
                <div className="node-circle purple-node"></div>
            </div>
            <div
                className="html-node"
                style={{ left: '69.44%', top: '36.66%', animationDelay: '1.8s' }}
            >
                <div className="node-circle purple-node"></div>
            </div>
            <div
                className="html-node"
                style={{ left: '83.33%', top: '13.33%', animationDelay: '2.1s' }}
            >
                <div className="node-circle blue-node"></div>
            </div>
            <div
                className="html-node head-node"
                style={{ left: '100%', top: '3.33%', animationDelay: '2.5s' }}
            >
                <div className="node-circle head-circle"></div>
                <div className="node-label end-label">1위 달성!</div>
            </div>
        </div>
    </div>
);

const Intro = () => {
    const navigate = useNavigate();
    const { count: queryCount, ref: queryRef } = useCountUp(850, 2000);
    const { count: slotCount, ref: slotRef } = useCountUp(98, 2000);
    const { count: dayCount, ref: dayRef } = useCountUp(30, 2000);

    return (
        <div className="intro-page-clean">
            {/* HERO - Minimalist White */}
            <section className="intro-hero-clean">
                {/* Top Navigation Bar */}
                <div
                    style={{
                        position: 'absolute',
                        top: '24px',
                        left: '32px',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                    }}
                >
                    <button
                        onClick={() => navigate('/login')}
                        style={{
                            background: 'rgba(255, 255, 255, 0.8)',
                            border: '1px solid rgba(0,0,0,0.1)',
                            padding: '8px 24px',
                            borderRadius: '24px',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#2e5bff',
                            cursor: 'pointer',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        }}
                        onMouseOver={e => {
                            e.currentTarget.style.background = '#2e5bff';
                            e.currentTarget.style.color = '#fff';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onFocus={e => {
                            e.currentTarget.style.background = '#2e5bff';
                            e.currentTarget.style.color = '#fff';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseOut={e => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                            e.currentTarget.style.color = '#2e5bff';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                        onBlur={e => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                            e.currentTarget.style.color = '#2e5bff';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        기존 사용자 로그인{' '}
                        <i className="fas fa-sign-in-alt" style={{ marginLeft: '4px' }} />
                    </button>
                </div>
                <HeroBackgroundChart />
                <div className="ihc-container">
                    <div className="ihc-content">
                        <div className="ihc-badge anim-chip">상위 1% 빅파워 셀러들의 치트키</div>
                        <h1 className="ihc-title anim-title">
                            광고 끄면 뚝 떨어지는 매출,
                            <br />
                            진짜 문제는 <span>&apos;자연 순위&apos;</span>입니다.
                        </h1>
                        <p className="ihc-desc anim-subtitle">
                            언제까지 네이버에 광고비만 갖다 바치실 겁니까?
                            <br />
                            정밀한 순위 추적과 압도적인 키워드 분석으로 광고 없이 1페이지를
                            장악하세요.
                        </p>
                        <div className="ihc-actions anim-btn">
                            <button className="ihc-btn-primary" onClick={() => navigate('/login')}>
                                지금 시작하기 <i className="fas fa-arrow-right" />
                            </button>
                            <button
                                className="ihc-btn-secondary"
                                onClick={() => window.open(KAKAO_CHANNEL_URL, '_blank')}
                            >
                                전문가 상담
                            </button>
                        </div>
                    </div>

                    {/* Visuals softly floating below */}
                    <div className="ihc-visual anim-btn">
                        <div className="ihc-card ihc-card-left">
                            <Preview_KeywordRank />
                        </div>
                        <div className="ihc-card ihc-card-right">
                            <Preview_ProductAnalysis />
                        </div>
                    </div>
                </div>
            </section>

            {/* CLEAN STATS */}
            <section className="intro-stats-clean">
                <div className="isc-container">
                    <div className="isc-item" ref={queryRef}>
                        <h3 className="isc-value">{queryCount}만+</h3>
                        <p className="isc-label">분석된 누적 키워드</p>
                    </div>
                    <div className="isc-divider"></div>
                    <div className="isc-item" ref={slotRef}>
                        <h3 className="isc-value">{slotCount}%</h3>
                        <p className="isc-label">고객 만족도 및 상위 달성률</p>
                    </div>
                    <div className="isc-divider"></div>
                    <div className="isc-item" ref={dayRef}>
                        <h3 className="isc-value">{dayCount}일</h3>
                        <p className="isc-label">이력 보관으로 트렌드 완벽 추적</p>
                    </div>
                </div>
            </section>

            {/* SHOWCASES */}
            <section className="intro-showcases-clean">
                <div className="intro-showcases-inner">
                    <div className="intro-section-head-clean">
                        <h2 className="intro-section-title">
                            데이터가 없으면 결국 운에 맡기는 겁니다
                        </h2>
                        <p className="intro-section-sub">
                            매출을 멱살 잡고 끌어올릴 ADCARE만의 압도적인 기능들
                        </p>
                    </div>
                    {SHOWCASES.map((sc, i) => (
                        <ShowcaseCard key={i} sc={sc} />
                    ))}
                </div>
            </section>

            {/* ALL FEATURES - CLEAN LIST */}
            <section className="intro-features-clean">
                <div className="ifc-container">
                    <div className="intro-section-head-clean">
                        <h2 className="intro-section-title">상위 노출을 위한 13가지 필수 무기</h2>
                        <p className="intro-section-sub">
                            단편적인 조회를 넘어, 경쟁사를 압도할 빈틈없는 분석을 제공합니다.
                        </p>
                    </div>
                    <div className="ifc-grid">
                        {FEATURES.map((f, i) => (
                            <FeatureItemClean key={f.title} f={f} delay={(i % 3) * 50} />
                        ))}
                    </div>
                </div>
            </section>

            <KakaoFloat />

            {/* CLEAN CTA */}
            <section className="intro-cta-clean">
                <div className="icm-content">
                    <h2>더 늦기 전에, 빼앗긴 순위를 되찾으세요</h2>
                    <p>
                        지금 이 순간에도 경쟁사는 당신의 키워드를 뺏어가고 있습니다.
                        <br />단 3초 만에 내 상품의 진짜 순위를 확인해 보세요.
                    </p>
                    <button className="ihc-btn-primary" onClick={() => navigate('/login')}>
                        내 상품 진짜 순위 확인하기 <i className="fas fa-arrow-right" />
                    </button>
                </div>
            </section>
        </div>
    );
};

export default Intro;
